import Moment from "moment";
import { AppointmentDto } from "appointment-experience-client";
import PhoneLink from "../../../components/PhoneLink";
import { ReactComponent as CopyIcon } from "../../../assets/img/copy-icon.svg";
import { Constants } from "../../../constants/Lookups";
import { appointmentStatusTone } from "./appointmentStatus";

interface AppointmentHeroProps {
  appointment: AppointmentDto;
  /** The patient's own numbers, most-preferred first. */
  phones: string[];
  /**
   * Final states are read-only: no start, return or cancel, and no join link.
   * The bubble still shows when and with whom the visit was.
   */
  isReadOnly: boolean;
  canCancel: boolean;
  isInOverlay: boolean;
  isOtherInOverlay: boolean;
  isStarting: boolean;
  onStart: () => void;
  onCancel: () => void;
  onCopyLink: (url: string) => void;
}

const dayLabel = (time: Moment.Moment): string => {
  const today = Moment().startOf("day");
  const diff = time.clone().startOf("day").diff(today, "days");
  if (diff === 0) return "Today";
  if (diff === 1) return "Tomorrow";
  if (diff === -1) return "Yesterday";
  return time.format("YYYY");
};

/** Short generic name of the viewer's time zone, e.g. "MT" (not "MST"/"MDT"). */
const timeZoneAbbreviation = (date: Date): string =>
  new Intl.DateTimeFormat("en-US", { timeZoneName: "shortGeneric" })
    .formatToParts(date)
    .find((part) => part.type === "timeZoneName")?.value ?? "";

const initials = (name: string): string =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join("");

export const AppointmentHero = ({
  appointment,
  phones,
  isReadOnly,
  canCancel,
  isInOverlay,
  isOtherInOverlay,
  isStarting,
  onStart,
  onCancel,
  onCopyLink,
}: AppointmentHeroProps) => {
  const start = Moment(appointment.time);
  const end = start.clone().add(appointment.durationMinutes ?? 0, "minutes");
  const coach = appointment.coaches?.[0];
  const coachName = coach?.coachName ?? "";
  const modeName = appointment.appointmentMode?.name;
  const isVideo = modeName === Constants.VIDEO_MODE;
  // Video visits run on Zoom and phone visits on Dialpad, so the mode wears that brand's colours.
  const modeBrand = isVideo
    ? "zoom"
    : modeName === Constants.TELEPHONE_MODE
      ? "dialpad"
      : null;
  // "Video Call" / "Telephone" read as "Video call" / "Phone call" here.
  const modeLabel =
    modeBrand === "zoom"
      ? "Video call"
      : modeBrand === "dialpad"
        ? "Phone call"
        : modeName;
  const statusName = appointment.status?.stateName ?? "";
  const title = [appointment.appointmentCategory?.name, appointment.appointmentType?.name]
    .filter(Boolean)
    .join(" · ");

  return (
    <div className="appointment-hero">
      <div className="appointment-hero__when">
        <div>
          <div className="appointment-hero__eyebrow">{dayLabel(start)}</div>
          <div className="appointment-hero__date">{start.format("ddd, MMM D")}</div>
        </div>
        <div className="appointment-hero__time-block">
          <div className="appointment-hero__time">
            {start.format("h:mm")}
            <span className="appointment-hero__meridiem">{start.format("A")}</span>
          </div>
          <div className="appointment-hero__until">
            to {end.format("h:mm A")} {timeZoneAbbreviation(end.toDate())}
          </div>
          <div className="appointment-hero__duration">
            <span className="material-symbols-outlined">schedule</span>
            {appointment.durationMinutes} minutes
          </div>
        </div>
      </div>

      <div className="appointment-hero__body">
        <span
          className={`appointment-status-pill appointment-status-pill--${appointmentStatusTone(statusName)}`}
        >
          {statusName}
        </span>

        <h2 className="appointment-hero__title">{title}</h2>

        {/* Mode through clinician, centred between the title and the actions. */}
        <div className="appointment-hero__center">
          <div
            className={`appointment-hero__mode${modeBrand ? ` appointment-hero__mode--${modeBrand}` : ""}`}
            data-testid="appointment-mode"
          >
            <span className="material-symbols-outlined">
              {isVideo ? "videocam" : "call"}
            </span>
            {modeLabel}
          </div>

          <div className="appointment-hero__contact">
            {isVideo
              ? !isReadOnly &&
                appointment.webinarUrl && (
                  <span className="appointment-hero__link">
                    <span
                      className="appointment-hero__link-text"
                      title={appointment.webinarUrl}
                    >
                      Patient link: {appointment.webinarUrl}
                    </span>
                    <button
                      type="button"
                      className="appointment-hero__copy"
                      aria-label="Copy patient link"
                      onClick={() => onCopyLink(appointment.webinarUrl ?? "")}
                    >
                      <CopyIcon />
                    </button>
                  </span>
                )
              : phones.map((phone) => (
                  <span key={phone}>
                    <PhoneLink phone={phone} className="appointment-hero__phone" />
                  </span>
                ))}
          </div>

          {coachName && (
            <>
              <div className="appointment-hero__with">with</div>
              <div className="appointment-hero__coach">
                {coach?.coachHeadshotUrl ? (
                  <img
                    src={coach.coachHeadshotUrl}
                    alt={coachName}
                    className="appointment-hero__avatar"
                  />
                ) : (
                  <span className="appointment-hero__avatar">{initials(coachName)}</span>
                )}
                <span className="appointment-hero__coach-name">{coachName}</span>
              </div>
            </>
          )}
        </div>

        {!isReadOnly && (
          <div className="appointment-hero__actions">
            {isInOverlay ? (
              // The visit is being run (and ended) from the appointment overlay,
              // so there's nothing to do here; just show that it's live.
              <>
                <div className="appointment-hero__live" role="status">
                  <span className="appointment-hero__live-dot" aria-hidden="true" />
                  <span>Appointment in progress</span>
                </div>
                <div className="appointment-hero__live-hint">
                  Complete the appointment from the overlay
                </div>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="appointment-hero__primary"
                  disabled={isOtherInOverlay || isStarting}
                  title={
                    isOtherInOverlay
                      ? "Another appointment is already in progress"
                      : undefined
                  }
                  onClick={onStart}
                >
                  {isStarting ? "Starting..." : "Start appointment"}
                </button>
                {canCancel && (
                  <button
                    type="button"
                    className="appointment-hero__cancel"
                    onClick={onCancel}
                  >
                    Cancel appointment
                  </button>
                )}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
